import { useMemo, useState } from 'react';
import { formatearCLP } from '@lexicore/shared';
import { IconoCerrar, IconoMas } from '../../componentes/Iconos';
import { useAvisos } from '../../contextos/AvisosContext';
import { useCategoriasGastoConfig, useProyectos } from '../../lib/consultas';
import { useCrearGastoDirecto } from '../../lib/mutaciones';

interface Props {
  alCerrar: () => void;
  alCrearExitoso?: () => void;
  proyectoIdPredeterminado?: string;
  ambitoInicial?: 'PROYECTO' | 'GENERAL';
}

export function ModalRegistrarGasto({
  alCerrar,
  alCrearExitoso,
  proyectoIdPredeterminado,
  ambitoInicial = 'PROYECTO',
}: Props) {
  const avisos = useAvisos();
  const proyectosQuery = useProyectos();
  const categoriasGastoQuery = useCategoriasGastoConfig(false);
  const crearGastoMut = useCrearGastoDirecto();

  const [ambito, setAmbito] = useState<'PROYECTO' | 'GENERAL'>(
    proyectoIdPredeterminado ? 'PROYECTO' : ambitoInicial,
  );
  const [proyectoId, setProyectoId] = useState<string>(proyectoIdPredeterminado ?? '');
  const [categoriaGastoId, setCategoriaGastoId] = useState<string>('');
  const [concepto, setConcepto] = useState<string>('');
  const [proveedorNombre, setProveedorNombre] = useState<string>('');
  const [proveedorRut, setProveedorRut] = useState<string>('');
  const [montoNetoInput, setMontoNetoInput] = useState<string>('');
  const [aplicaIva, setAplicaIva] = useState<boolean>(true);
  const [tipoDocumento, setTipoDocumento] = useState<string>('FACTURA');
  const [numeroDocumento, setNumeroDocumento] = useState<string>('');
  const [estadoPago, setEstadoPago] = useState<'PENDIENTE' | 'PAGADO'>('PENDIENTE');
  const [fecha, setFecha] = useState<string>(new Date().toISOString().slice(0, 10));

  // Cálculos en tiempo real
  const montoNeto = useMemo(() => {
    const num = parseInt(montoNetoInput.replace(/\D/g, ''), 10);
    return isNaN(num) ? 0 : num;
  }, [montoNetoInput]);

  const ivaPct = aplicaIva ? 19 : 0;
  const montoIva = Math.round(montoNeto * (ivaPct / 100));
  const montoTotal = montoNeto + montoIva;

  // Filtrar categorías según ámbito sugerido
  const categoriasDisponibles = useMemo(() => {
    const todas = categoriasGastoQuery.data ?? [];
    if (ambito === 'PROYECTO') {
      return [...todas].sort((a, b) => (a.tipo === 'DIRECTO' ? -1 : 1));
    }
    return [...todas].sort((a, b) => (a.tipo === 'INDIRECTO' ? -1 : 1));
  }, [categoriasGastoQuery.data, ambito]);

  const proyectosActivos = useMemo(() => {
    return (proyectosQuery.data ?? []).filter((p) => p.estado !== 'CERRADO' && p.estado !== 'CANCELADO');
  }, [proyectosQuery.data]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!concepto.trim()) {
      avisos.error('Por favor ingresa un concepto o descripción para el gasto.');
      return;
    }

    if (!categoriaGastoId) {
      avisos.error('Selecciona una categoría de gasto válida.');
      return;
    }

    if (ambito === 'PROYECTO' && !proyectoId) {
      avisos.error('Selecciona el proyecto al cual se imputará este costo directo.');
      return;
    }

    if (montoNeto <= 0) {
      avisos.error('El monto neto del gasto debe ser mayor a $0.');
      return;
    }

    try {
      await crearGastoMut.mutateAsync({
        ambito,
        proyectoId: ambito === 'PROYECTO' ? proyectoId : null,
        categoriaGastoId,
        concepto: concepto.trim(),
        proveedorNombre: proveedorNombre.trim() || undefined,
        proveedorRut: proveedorRut.trim() || undefined,
        montoNeto,
        ivaPct,
        tipoDocumento,
        numeroDocumento: numeroDocumento.trim() || undefined,
        estadoPago,
        fecha,
      });

      avisos.exito('Gasto registrado exitosamente.');
      alCrearExitoso?.();
      alCerrar();
    } catch (err) {
      avisos.error(err instanceof Error ? err.message : 'No se pudo registrar el gasto.');
    }
  };

  return (
    <div className="modal-overlay" onClick={alCerrar}>
      <div
        className="modal"
        style={{ maxWidth: 640, width: '100%', maxHeight: '92vh', display: 'flex', flexDirection: 'column' }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Cabecera */}
        <div className="modal-header">
          <div>
            <h2 style={{ fontSize: 18, fontWeight: 700, margin: 0, color: 'var(--text-primary)' }}>
              Registrar Gasto o Egreso
            </h2>
            <p style={{ fontSize: 12, color: 'var(--text-muted)', margin: '4px 0 0' }}>
              Imputación rápida de costos directos a proyectos o gastos de estructura empresarial.
            </p>
          </div>
          <button type="button" className="btn-icono" onClick={alCerrar} aria-label="Cerrar modal">
            <IconoCerrar size={16} />
          </button>
        </div>

        {/* Selector de Ámbito */}
        <div style={{ padding: '16px 24px 0' }}>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: 8,
              background: 'var(--bg-elevated)',
              padding: 4,
              borderRadius: 'var(--radius-md)',
            }}
          >
            <button
              type="button"
              className={`btn btn-sm ${ambito === 'PROYECTO' ? 'btn-primary' : 'btn-secondary'}`}
              onClick={() => setAmbito('PROYECTO')}
              style={{
                justifyContent: 'center',
                padding: '8px 12px',
                fontWeight: 600,
                fontSize: 12.5,
              }}
            >
              🎯 Costo Directo (Proyecto)
            </button>
            <button
              type="button"
              className={`btn btn-sm ${ambito === 'GENERAL' ? 'btn-primary' : 'btn-secondary'}`}
              onClick={() => {
                setAmbito('GENERAL');
                setProyectoId('');
              }}
              style={{
                justifyContent: 'center',
                padding: '8px 12px',
                fontWeight: 600,
                fontSize: 12.5,
              }}
            >
              🏢 Gasto General (Estructura)
            </button>
          </div>
        </div>

        {/* Formulario */}
        <form onSubmit={handleSubmit} style={{ overflowY: 'auto', padding: '16px 24px', flex: 1 }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            {/* Si es de proyecto, selector de proyecto */}
            {ambito === 'PROYECTO' && (
              <div className="form-group">
                <label htmlFor="gasto_proyecto_id">
                  Proyecto Asignado <span style={{ color: 'var(--accent-red)' }}>*</span>
                </label>
                <select
                  id="gasto_proyecto_id"
                  value={proyectoId}
                  onChange={(e) => setProyectoId(e.target.value)}
                  required
                >
                  <option value="">Selecciona un proyecto activo…</option>
                  {proyectosActivos.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.codigo} — {p.nombre} ({p.licitacion?.organismo.nombre ?? p.organismo?.nombre ?? 'Cliente'})
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Categoría y Tipo de Documento */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 12 }}>
              <div className="form-group">
                <label htmlFor="gasto_cat_id">
                  Categoría de Costo <span style={{ color: 'var(--accent-red)' }}>*</span>
                </label>
                <select
                  id="gasto_cat_id"
                  value={categoriaGastoId}
                  onChange={(e) => setCategoriaGastoId(e.target.value)}
                  required
                >
                  <option value="">Selecciona una categoría…</option>
                  {categoriasDisponibles.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.nombre} ({c.tipo === 'DIRECTO' ? 'Costo Directo' : 'Gasto Indirecto'})
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="gasto_tipo_doc">Tipo de Comprobante</label>
                <select
                  id="gasto_tipo_doc"
                  value={tipoDocumento}
                  onChange={(e) => setTipoDocumento(e.target.value)}
                >
                  <option value="FACTURA">Factura de Compra / Proveedor</option>
                  <option value="BOLETA_HONORARIOS">Boleta de Honorarios (Exenta)</option>
                  <option value="BOLETA_COMPRA">Boleta de Compra / Insumos</option>
                  <option value="COMPROBANTE">Comprobante de Transferencia</option>
                  <option value="OTRO">Otro Documento</option>
                </select>
              </div>
            </div>

            {/* Concepto / Glosa */}
            <div className="form-group">
              <label htmlFor="gasto_concepto">
                Concepto / Glosa del Gasto <span style={{ color: 'var(--accent-red)' }}>*</span>
              </label>
              <input
                id="gasto_concepto"
                type="text"
                placeholder="Ej: Servidores AWS, Materiales de tendido fibra, Arriendo oficina…"
                value={concepto}
                onChange={(e) => setConcepto(e.target.value)}
                required
              />
            </div>

            {/* Proveedor y Folio */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12 }}>
              <div className="form-group" style={{ flex: 2 }}>
                <label htmlFor="gasto_proveedor">Proveedor / Razón Social</label>
                <input
                  id="gasto_proveedor"
                  type="text"
                  placeholder="Ej: Sonda S.A. o Inmobiliaria SpA"
                  value={proveedorNombre}
                  onChange={(e) => setProveedorNombre(e.target.value)}
                />
              </div>

              <div className="form-group">
                <label htmlFor="gasto_rut">RUT Proveedor</label>
                <input
                  id="gasto_rut"
                  type="text"
                  placeholder="76.123.456-K"
                  value={proveedorRut}
                  onChange={(e) => setProveedorRut(e.target.value)}
                />
              </div>

              <div className="form-group">
                <label htmlFor="gasto_folio">N° Folio / Factura</label>
                <input
                  id="gasto_folio"
                  type="text"
                  placeholder="Ej: 14820"
                  value={numeroDocumento}
                  onChange={(e) => setNumeroDocumento(e.target.value)}
                />
              </div>
            </div>

            {/* Importes y Calculadora de IVA */}
            <div
              style={{
                background: 'var(--bg-elevated)',
                border: '1px solid var(--border-subtle)',
                borderRadius: 'var(--radius-md)',
                padding: '14px 16px',
                display: 'flex',
                flexDirection: 'column',
                gap: 12,
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
                <strong style={{ fontSize: 13, color: 'var(--text-primary)' }}>Montos Tributarios</strong>
                <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={aplicaIva}
                    onChange={(e) => setAplicaIva(e.target.checked)}
                  />
                  <span>Afecto a 19% IVA</span>
                </label>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12 }}>
                <div className="form-group">
                  <label htmlFor="gasto_monto_neto">
                    Monto Neto (CLP) <span style={{ color: 'var(--accent-red)' }}>*</span>
                  </label>
                  <input
                    id="gasto_monto_neto"
                    type="text"
                    placeholder="$0"
                    value={montoNeto > 0 ? formatearCLP(montoNeto) : montoNetoInput}
                    onChange={(e) => setMontoNetoInput(e.target.value)}
                    required
                    style={{ fontFamily: 'var(--font-mono)', fontSize: 13, fontWeight: 600 }}
                  />
                </div>

                <div className="form-group">
                  <label>IVA ({ivaPct}%)</label>
                  <input
                    type="text"
                    value={formatearCLP(montoIva)}
                    disabled
                    style={{
                      fontFamily: 'var(--font-mono)',
                      fontSize: 13,
                      background: 'transparent',
                      color: 'var(--text-muted)',
                    }}
                  />
                </div>

                <div className="form-group">
                  <label>Total Bruto a Pagar</label>
                  <input
                    type="text"
                    value={formatearCLP(montoTotal)}
                    disabled
                    style={{
                      fontFamily: 'var(--font-mono)',
                      fontSize: 13,
                      fontWeight: 700,
                      background: 'var(--bg-card)',
                      color: 'var(--accent-green)',
                    }}
                  />
                </div>
              </div>
            </div>

            {/* Fecha y Estado de Pago */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div className="form-group">
                <label htmlFor="gasto_fecha">Fecha de Emisión / Gasto</label>
                <input
                  id="gasto_fecha"
                  type="date"
                  value={fecha}
                  onChange={(e) => setFecha(e.target.value)}
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="gasto_estado_pago">Estado de Pago Inicial</label>
                <select
                  id="gasto_estado_pago"
                  value={estadoPago}
                  onChange={(e) => setEstadoPago(e.target.value as 'PENDIENTE' | 'PAGADO')}
                >
                  <option value="PENDIENTE">Pendiente (Cuenta por pagar)</option>
                  <option value="PAGADO">Pagado (Transferido)</option>
                </select>
              </div>
            </div>
          </div>

          {/* Botones de acción */}
          <div className="modal-footer" style={{ padding: '16px 0 0', marginTop: 16 }}>
            <button type="button" className="btn btn-secondary" onClick={alCerrar} disabled={crearGastoMut.isPending}>
              Cancelar
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={crearGastoMut.isPending || montoNeto <= 0}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
            >
              <IconoMas size={15} />
              <span>{crearGastoMut.isPending ? 'Guardando…' : 'Registrar Gasto'}</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
